import { FC, useEffect, useState, useMemo, useCallback } from 'react';

// API
import apibridge, { AssessmentDashboardParamsType, GetAssessmentOverviewTypes } from '../../apibridge';

// REDUX
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';

// PACKAGES
import { Modal, Offcanvas, Spinner, Dropdown } from 'react-bootstrap';

// UTILS
import { guid } from '../../libs/utils';

// COMPONENTS
import DelayedFadeIn from '../../components/_Helpers/DelayedFadeIn';
import TitleSection, { TitleSectionType } from '../../components/TitleSection/TitleSection';
import SvgMask from '../../components/_Helpers/SvgMask';
import SortDropdown, { SortOptionsType } from '../../components/TitleSection/SortDropdown';
import AssessmentDashboard from '../../components/Assessment/Dashboard/AssessmentDashboard';
import StageOverviewTable from '../../components/Assessment/ResultTable/StageOverviewTable';
import AssessmentOverviewTable from '../../components/Assessment/ResultTable/AssessmentOverviewTable';
import SpellingTable from '../../components/Assessment/ResultTable/SpellingTable';
import ReadingTable from '../../components/Assessment/ResultTable/ReadingTable';
import GraphemePhonemeTable from '../../components/Assessment/ResultTable/GraphemePhonemeTable';
import StudentProgressTable from '../../components/Assessment/ResultTable/StudentProgressTable';
import Divider from '../../components/Divider/Divider';
import MoveAssessmentMultiple from './MoveAssessment/MoveAssessmentMultiple';

// TYPES
import {
	AssessmentAssessmentsAssessment,
	AssessmentBenchmarkLevel,
	AssessmentClassesClass,
	AssessmentResourceListListResult,
	AssessmentStudentsAssessmentPeriod,
	AssessmentStudentsStudent,
	AssessmentStudentsType,
	AssessmentSummaryListSummaryInfo,
	AssessmentType
} from '../../api/models';

import {
	DynamicFtux,
	FtuxStep,
	getAssessmentsConfig,
	useFtuxHighlight,
	useDynamicFtuxRestart
} from '../../components/Ftux';
import { FtuxSlice } from '../../store/slice/Ftux';

export type TabStatus = 'Dashboard' | 'Results';

export type AssessmentResultTypeFilter =
	| 'AssessmentsOverview'
	| 'GraphemePhonemeKnowledge'
	| 'Reading'
	| 'Spelling'
	| 'StageOverview'
	| 'Progress';

export type AssessmentRecordOptionsType = {
	class: string;
	student: string;
	assessmentType: string;
	assessmentPeriod: string;
};

const Assessments: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Get Details from params
	const [searchParams] = useSearchParams();
	const tabView = searchParams.get('tabView') || '';

	// State to control the Tab view
	const [tabStatus, setTabStatus] = useState<TabStatus>('Dashboard');

	useEffect(() => {
		if (tabView) {
			setTabStatus(tabView as TabStatus);
		}
	}, [tabView]);

	// Loading state
	const [isLoading, setIsLoading] = useState(true);
	const [isAssessmentLoading, setIsAssessmentLoading] = useState(false);

	// State to show/hide Modal and OffCanvas
	const [showEditAssessmentOffCanvas, setShowEditAssessmentOffCanvas] = useState(false);
	const [showResourceModal, setShowResourceModal] = useState(false);

	const shouldHighlightAssessmentButtons = useFtuxHighlight('add-assessment-record-button', 'assessments-ftux');
	const shouldHighlightPreselectStudentButton = useFtuxHighlight('preselect-student-button', 'assessments-ftux');
	const shouldHighlightDashboardViewToggle = useFtuxHighlight('dashboard-view-toggle', 'assessments-ftux');
	const shouldHighlightClassAndAssessmentPeriodFilters = useFtuxHighlight(
		'class-and-assessment-period-filters',
		'assessments-ftux'
	);
	const shouldHighlightTableViewToggle = useFtuxHighlight('table-view-toggle', 'assessments-ftux');
	const shouldHighlightAssessmentExportButton = useFtuxHighlight('assessment-export-button', 'assessments-ftux');
	const shouldHighlightBenchmarkFilter = useFtuxHighlight('benchmark-filter', 'assessments-ftux');
	const shouldHighlightAssessmentResourcesButton = useFtuxHighlight('assessment-resources-button', 'assessments-ftux');

	const { restartTutorial, manualRestartTrigger } = useDynamicFtuxRestart('Assessments');

	// State to control export dropdown open/close
	const [exportDropdownOpen, setExportDropdownOpen] = useState<boolean>(false);

	// Open dropdown when shouldHighlightAssessmentExportButton becomes true
	useEffect(() => {
		if (shouldHighlightAssessmentExportButton) {
			setExportDropdownOpen(true);
		}
	}, [shouldHighlightAssessmentExportButton]);

	// Title Props set up
	const titleSectionProps: TitleSectionType = {
		title: 'Assessments',
		description: 'Dashboard for recording and analysing assessments.'
	};

	titleSectionProps.primaryButtonText = 'Add assessment record';
	titleSectionProps.primaryButtonIcon = '/svg/plus.svg';
	titleSectionProps.primaryButtonClick = () => setShowEditAssessmentOffCanvas(true);
	titleSectionProps.primaryButtonClickId = 'add-assessment-record-button';
	titleSectionProps.primaryButtonHighlight = shouldHighlightAssessmentButtons;

	titleSectionProps.secondaryButtonText = 'Assessment resources';
	titleSectionProps.secondaryButtonIcon = '/svg/download.svg';
	titleSectionProps.secondaryButtonClickId = 'assessment-resources-button';
	titleSectionProps.secondaryButtonHighlight = shouldHighlightAssessmentResourcesButton;
	titleSectionProps.secondaryButtonClick = () => setShowResourceModal(true);

	// State to control Dropdown options for Adding Assessment Record
	const defaultAssessmentRecordOptions = {
		class: '',
		student: '',
		assessmentType: '',
		assessmentPeriod: ''
	};

	const [assessmentRecordOptions, setAssessmentRecordOptions] =
		useState<AssessmentRecordOptionsType>(defaultAssessmentRecordOptions);

	// Filter States
	const [assessmentTypeFilter, setAssessmentTypeFilter] = useState<AssessmentResultTypeFilter>('AssessmentsOverview');

	const [benchmarkLevel, setBenchmarkLevel] = useState<AssessmentBenchmarkLevel>();

	const [selectedViewOption, setSelectedViewsOption] = useState<SortOptionsType>({
		label: '',
		value: ''
	});

	const [selectedAssessmentPeriodOption, setSelectedAssessmentPeriodOption] = useState<SortOptionsType>({
		label: '',
		value: ''
	});

	const [summaryListData, setSummaryListData] = useState<AssessmentSummaryListSummaryInfo>();

	const { assessmentPeriods = [], benchmarks = [], resultTypes = [], views = [] } = summaryListData || {};

	// State for Resource Documents
	const [resourceDocuments, setResourceDocuments] = useState<AssessmentResourceListListResult>({});

	// All Select Dropdowns State
	const assessmentRecordsDropdownListsDefault: {
		classList: AssessmentClassesClass[];
		studentList: AssessmentStudentsStudent[];
		assessmentList: AssessmentAssessmentsAssessment[];
		assessmentPeriodList: AssessmentStudentsAssessmentPeriod[];
		assessmentPeriodTypeList: AssessmentStudentsType[];
	} = {
		classList: [],
		studentList: [],
		assessmentList: [],
		assessmentPeriodList: [],
		assessmentPeriodTypeList: []
	};

	const [assessmentRecordsDropdownLists, setAssessmentRecordsDropdownLists] = useState(
		assessmentRecordsDropdownListsDefault
	);

	// All Filter Params State
	const [assessmentDashboardParams, setAssessmentDashboardParams] = useState<AssessmentDashboardParamsType>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key
	});

	const [assessmentSummaryParams, setAssessmentSummaryParams] = useState<
		GetAssessmentOverviewTypes['StageOverviewType']
	>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortStageId: undefined,
		benchmark: undefined
	});

	const [assessmentGraphemePhonemeParams, setAssessmentGraphemePhonemeParams] = useState<
		GetAssessmentOverviewTypes['GraphemePhonemeType']
	>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortLetter: undefined,
		sortStageId: undefined,
		benchmark: undefined
	});

	const [assessmentReadingTableParams, setAssessmentReadingTableParams] = useState<
		GetAssessmentOverviewTypes['ReadingType']
	>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortText: undefined,
		sortStageId: undefined,
		benchmark: undefined
	});

	const [assessmentSpellingTableParams, setAssessmentSpellingTableParams] = useState<
		GetAssessmentOverviewTypes['SpellingType']
	>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortLetter: undefined,
		sortStageId: undefined,
		benchmark: undefined
	});

	const [assessmentStageOverviewParams, setAssessmentStageOverviewParams] = useState<
		GetAssessmentOverviewTypes['StageOverviewType']
	>({
		_class: selectedViewOption.value,
		assessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortStageId: undefined,
		benchmark: undefined
	});

	const [assessmentStudentProgressParams, setAssessmentStudentProgressParams] = useState<
		GetAssessmentOverviewTypes['StudentProgressType']
	>({
		_class: selectedViewOption.value,
		sortAssessmentPeriodId: assessmentPeriods?.[0]?.key,
		pageNumber: 1,
		sortType: 'Student',
		sortOrder: 'Ascending',
		sortStageId: undefined
	});

	// Logic to detect if an Assessment has been created, then create a new one or navigate to existing one accordingly
	const navigateToRecord = async (assessment: AssessmentAssessmentsAssessment) => {
		if (!assessment) return;

		const isAssessmentReading = assessment.type === AssessmentType.Llars;
		const assessmentName = assessment.name;

		if (assessment.recordId) {
			navigate(
				`/assessment-management/${isAssessmentReading ? 'reading' : 'spelling'}?recordId=${
					assessment.recordId
				}&assessmentName=${encodeURIComponent(assessmentName || '')}`
			);
		} else {
			setIsAssessmentLoading(true);

			const submitData = {
				type: (assessmentRecordOptions.assessmentType as AssessmentType) || '',
				assessmentId: assessment.id,
				studentId: assessmentRecordOptions.student,
				assessmentPeriodId: assessmentRecordOptions.assessmentPeriod
			};

			const response = await apibridge.postAssessmentCreate(submitData);

			if (response && response.data) {
				if (!response.data.isError && response.data.result) {
					const recordId = response.data.result.id;

					navigate(
						`/assessment-management/${
							isAssessmentReading ? 'reading' : 'spelling'
						}?recordId=${recordId}&assessmentName=${encodeURIComponent(assessmentName || '')}`
					);
				} else if (response.data.validationErrors) {
					for (const err of response.data.validationErrors) {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Create Assessment error',
								description: err.reason || 'Unknown error'
							})
						);
					}
				}
			}

			setIsAssessmentLoading(false);
		}
	};

	const getAssessmentResourceList = async () => {
		const response = await apibridge.getAssessmentResourceList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setResourceDocuments(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Resource error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	const handleStudentClick = async (classId: string, studentId: string) => {
		setShowEditAssessmentOffCanvas(true);
		setAssessmentRecordOptions({
			...defaultAssessmentRecordOptions,
			class: classId,
			student: studentId
		});
	};

	const getAssessmentClassesData = async () => {
		setIsAssessmentLoading(true);

		const response = await apibridge.getAssessmentClasses();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentRecordsDropdownLists({
					...assessmentRecordsDropdownLists,
					classList: response.data.result?.classes || []
				});
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Class List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsAssessmentLoading(false);
	};

	const getAssessmentStudentsData = async (classId: string) => {
		setIsAssessmentLoading(true);

		const response = await apibridge.getAssessmentStudents(classId);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentRecordsDropdownLists({
					...assessmentRecordsDropdownLists,
					studentList: response.data.result?.students || [],
					assessmentPeriodList: response.data.result?.assessmentPeriods || [],
					assessmentPeriodTypeList: response.data.result?.assessmentTypes || []
				});
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Student List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsAssessmentLoading(false);
	};

	const getAssessmentListData = async () => {
		setIsAssessmentLoading(true);

		const response = await apibridge.getAssessmentAssessments(
			assessmentRecordOptions.student,
			(assessmentRecordOptions.assessmentType as AssessmentType) || 'Llars',
			assessmentRecordOptions.assessmentPeriod
		);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				const { assessments = [] } = response.data.result;
				setAssessmentRecordsDropdownLists({
					...assessmentRecordsDropdownLists,
					assessmentList: assessments
				});
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsAssessmentLoading(false);
	};

	const getSummaryListData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAssessmentSummaryList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				const result = {
					...response.data.result,
					views: [
						{
							key: '',
							label: 'All classes'
						},
						...(response.data.result.views || [])
					],
					assessmentPeriods: [
						{
							key: '',
							label: 'All assessment periods'
						},
						...(response.data.result.assessmentPeriods || [])
					]
				};

				setSummaryListData(result);

				const { views = [], assessmentPeriods = [] } = result;

				const viewsArr = views.map(({ key = '', label = '' }) => ({ value: key, label: label }));
				const assessmentPeriodsArr = assessmentPeriods.map(({ key = '', label = '' }) => ({
					value: key,
					label: label
				}));

				setSelectedViewsOption(viewsArr[0]);
				setSelectedAssessmentPeriodOption(assessmentPeriodsArr[0]);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Summary list error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	// this should have been a URL switch with an <Outlet /> for the assessment type, but it's already using query params...
	// Records the query params when clicking on the tabs
	const recordQueryParams = useCallback(
		(tabView: TabStatus) => {
			const searchParams = new URLSearchParams(location.search);

			// Clear all existing query parameters
			searchParams.forEach((_, key) => searchParams.delete(key));

			// Set the new query parameter
			searchParams.set('tabView', tabView);

			// Navigate to the new URL with the updated query string
			navigate({
				pathname: location.pathname,
				search: searchParams.toString()
			});
		},
		[navigate, location.pathname, location.search]
	);

	const resumeFtuxWhenReady = useCallback(() => {
		// Wait for React to render the new tab content, then resume FTUX
		// Using requestAnimationFrame ensures DOM is updated before checking
		requestAnimationFrame(() => {
			// Small delay to ensure the element is rendered and visible
			setTimeout(() => {
				dispatch(FtuxSlice.actions.resume());
			}, 100);
		});
	}, [dispatch]);

	const assessmentsConfig = useMemo(() => {
		const config = getAssessmentsConfig();
		const originalOnStepNext = config.onStepNext;

		return {
			...config,
			onStepNext: (step: FtuxStep, stepIndex: number) => {
				if (originalOnStepNext) {
					originalOnStepNext(step, stepIndex);
				}

				if (step.target === '#add-assessment-record-button') {
					dispatch(FtuxSlice.actions.pause());

					setTabStatus('Results');
					recordQueryParams('Results');

					resumeFtuxWhenReady();
				}
			}
		};
	}, [dispatch, recordQueryParams, resumeFtuxWhenReady]);

	// when 'View' or Assessment Period' change at top, update params for all different tables
	// needs to be done since those dropdowns are in the parent of those components
	useEffect(() => {
		setAssessmentDashboardParams({
			...assessmentDashboardParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value
		});

		setAssessmentSummaryParams({
			...assessmentSummaryParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		});

		setAssessmentGraphemePhonemeParams({
			...assessmentGraphemePhonemeParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		});

		setAssessmentReadingTableParams({
			...assessmentReadingTableParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		});

		setAssessmentSpellingTableParams({
			...assessmentSpellingTableParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		});

		setAssessmentStageOverviewParams({
			...assessmentStageOverviewParams,
			_class: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		});

		setAssessmentStudentProgressParams({
			...assessmentStudentProgressParams,
			_class: selectedViewOption.value,
			sortAssessmentPeriodId: selectedAssessmentPeriodOption.value
		});
	}, [selectedViewOption, selectedAssessmentPeriodOption, benchmarkLevel]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (assessmentRecordOptions.assessmentPeriod) {
			getAssessmentListData();
		}
	}, [assessmentRecordOptions.assessmentPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (assessmentRecordOptions.class) {
			getAssessmentStudentsData(assessmentRecordOptions.class);
		}
	}, [assessmentRecordOptions.class]); // eslint-disable-line react-hooks/exhaustive-deps

	// Find all class IDs
	const allClassIDs = assessmentRecordsDropdownLists.classList.map((classDetail) => classDetail.id?.toLowerCase());

	// When the view option changes, update the assessment record options
	useEffect(() => {
		if (selectedViewOption.value) {
			// If the selected view option is a class ID, update the assessment record options
			if (allClassIDs.includes(selectedViewOption.value.toLowerCase())) {
				setAssessmentRecordOptions({
					...defaultAssessmentRecordOptions,
					class: selectedViewOption.value.toLowerCase()
				});
			}
		}
	}, [selectedViewOption.value]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getAssessmentClassesData();
		getAssessmentResourceList();
		getSummaryListData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Export Excel
	const [isExporting, setIsExporting] = useState(false);

	const confirmExport = async () => {
		setIsExporting(true);

		const submitData = {
			classId: selectedViewOption.value,
			assessmentPeriodId: selectedAssessmentPeriodOption.value,
			benchmark: benchmarkLevel
		};

		try {
			const response = await apibridge.postAssessmentExportList(submitData);

			if (response && response.data) {
				if (!response.data.isError && response.data.result) {
					const { contentType: mimeType, file: bytesBase64, fileName } = response.data.result;

					// construct a url from the base64 and then fetch it like a file to access the blob method
					const fileUrl = 'data:' + mimeType + ';base64,' + bytesBase64;
					const fileResponse = await fetch(fileUrl);

					let blob;

					if (fileResponse && fileResponse.ok) {
						blob = await fileResponse.blob();
					}

					if (blob) {
						const url = window.URL.createObjectURL(blob);

						const a = document.createElement('a');

						a.style.display = 'none';

						a.href = url;

						a.download = fileName || 'assessment-export.xlsx';

						document.body.appendChild(a);

						a.click();

						document.body.removeChild(a);

						setTimeout(() => window.URL.revokeObjectURL(url), 100);
					} else {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Export failed',
								description: ''
							})
						);
						console.error('Blob creation failed');
					}
				} else if (response.data.validationErrors) {
					for (const err of response.data.validationErrors) {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Export error',
								description: err.reason || 'Unknown error'
							})
						);
					}
				}
			}
		} catch (error) {
			dispatch(
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Export failed',
					description: ''
				})
			);
			console.error('Export failed:', error);
		}

		setIsExporting(false);
	};

	// When the assessment type filter changes to Progress, only reset the assessment period and benchmark level
	useEffect(() => {
		if (assessmentTypeFilter === 'Progress') {
			if (!summaryListData) return;
			const { assessmentPeriods = [] } = summaryListData;

			const assessmentPeriodsArr = assessmentPeriods.map(({ key = '', label = '' }) => ({
				value: key,
				label: label
			}));

			// Only reset assessment period to "All assessment periods"
			// Keep the current view selection
			setSelectedAssessmentPeriodOption(assessmentPeriodsArr[0]);

			// Set to "All benchmarks" option
			setBenchmarkLevel('' as AssessmentBenchmarkLevel);
		}
	}, [assessmentTypeFilter, summaryListData]);

	// MOVE RECORD LOGIC
	const [showMoveRecordCheckboxes, setShowMoveRecordCheckboxes] = useState(false);
	const [showMoveAssessmentsModal, setShowMoveAssessmentsModal] = useState(false);
	const [selectedMovingStudents, setSelectedMovingStudents] = useState<string[]>([]);

	// Handle successful move - reset state
	const handleMoveSuccess = () => {
		setShowMoveRecordCheckboxes(false);
		setSelectedMovingStudents([]);
	};

	return (
		<div className="assessment-page">
			<TitleSection {...titleSectionProps} isHelpSection={true} onHelpClick={() => restartTutorial()}>
				<div className="row align-items-center row-gap-3">
					<div id="class-and-assessment-period-filters" className="col-auto d-flex flex-wrap gap-3 align-items-center">
						<div className={`${shouldHighlightClassAndAssessmentPeriodFilters ? 'highlight-outline rounded-3' : ''}`}>
							<div className="row align-items-center">
								<div className="col-auto">
									<span className="h4 text-nowrap">
										<strong>View</strong>
									</span>
								</div>

								{views.length > 0 && (
									<div className="col-auto">
										<SortDropdown
											sortOptions={views.map((view) => ({ label: view.label || '', value: view.key || '' }))}
											selectedSortOption={selectedViewOption}
											setSelectedSortOption={setSelectedViewsOption}
										/>
									</div>
								)}

								{assessmentPeriods.length > 0 && (
									<div className="col-auto">
										<SortDropdown
											sortOptions={assessmentPeriods.map((period) => ({
												label: period.label || '',
												value: period.key || ''
											}))}
											selectedSortOption={selectedAssessmentPeriodOption}
											setSelectedSortOption={setSelectedAssessmentPeriodOption}
											disabled={assessmentTypeFilter === 'Progress'}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-md-12 col-lg order-lg-first">
						<div className="position-relative w-auto align-items-center ">
							<nav
								id={'dashboard-view-toggle'}
								className={`nav nav-pills d-inline-flex flex-row flex-nowrap ${
									shouldHighlightDashboardViewToggle ? 'highlight-outline rounded-3' : ''
								}`}
							>
								<button
									onClick={() => {
										setTabStatus('Dashboard');
										setAssessmentTypeFilter('AssessmentsOverview');
										setBenchmarkLevel(undefined);
										recordQueryParams('Dashboard');
									}}
									className={`nav-pill h4 ${tabStatus === 'Dashboard' ? 'active' : ''} `}
								>
									<SvgMask path="/svg/pie-chart.svg" width={16} height={16} />
									Dashboard
								</button>

								<button
									onClick={() => {
										setTabStatus('Results');
										recordQueryParams('Results');
									}}
									className={`nav-pill h4 ${tabStatus === 'Results' ? 'active' : ''}`}
								>
									<SvgMask path="/svg/table-chart.svg" width={16} height={16} />
									Student Results
								</button>
							</nav>
						</div>
					</div>
				</div>
			</TitleSection>

			<div className="container">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : (
					<>
						{isExporting && (
							<div className="exporting-spinner-wrapper">
								<div className="content-wrappper">
									<Spinner animation="border" role="status" style={{ width: 48, height: 48 }}>
										<span className="visually-hidden">Exporting...</span>
									</Spinner>

									<p className="mb-0">Generating spreadsheet report...</p>
								</div>
							</div>
						)}

						{tabStatus === 'Dashboard' ? (
							<AssessmentDashboard
								assessmentDashboardParams={assessmentDashboardParams}
								setBenchmarkLevel={setBenchmarkLevel}
								setAssessmentTypeFilter={setAssessmentTypeFilter}
								setTabStatus={setTabStatus}
								recordQueryParams={recordQueryParams}
								selectedViewOption={selectedViewOption}
							/>
						) : tabStatus === 'Results' ? (
							<>
								<div className="mt-5 w-100">
									<div className="d-flex flex-column flex-lg-row justify-content-lg-between">
										{showMoveRecordCheckboxes ? (
											<div className="d-flex align-items-center">
												<div className="assessment-multiple-move-box">
													<button
														className="btn btn-icon btn-close-box"
														onClick={() => {
															setShowMoveRecordCheckboxes(false);
															setSelectedMovingStudents([]);
														}}
													>
														<SvgMask path="/svg/cross.svg" width={16} height={16} />
													</button>

													{selectedMovingStudents.length > 0 ? (
														<div className="d-flex gap-3 align-items-center">
															<p className="mb-0 text-white body-normal">
																{selectedMovingStudents.length} student{selectedMovingStudents.length === 1 ? '' : 's'}{' '}
																selected
															</p>

															<button
																className="btn btn-white btn-move-record-multiple"
																onClick={() => setShowMoveAssessmentsModal(true)}
															>
																Move records
															</button>
														</div>
													) : (
														<p className="mb-0 me-4 text-white body-normal">
															Select students to move records between periods
														</p>
													)}
												</div>
											</div>
										) : (
											<div className="d-flex gap-3 justify-content-flex-end align-items-center">
												<div id="table-view-toggle" className="form-group assessment-filter-select-wrapper mb-4">
													<label htmlFor="resultsType" className="body-small mb-1">
														<strong>Results type</strong>
													</label>

													<select
														value={assessmentTypeFilter}
														onChange={(e) => setAssessmentTypeFilter(e.target.value as AssessmentResultTypeFilter)}
														className={`form-select ${
															shouldHighlightTableViewToggle ? 'highlight-outline-secondary' : ''
														}`}
														name="className"
														id="resultsType"
													>
														{resultTypes.map((resultType, index) => {
															const { key, label } = resultType;
															return (
																<option key={index} value={key}>
																	{label}
																</option>
															);
														})}
													</select>
												</div>

												<Dropdown
													className="assessment-move-record-dropdown"
													show={exportDropdownOpen}
													onToggle={setExportDropdownOpen}
												>
													<Dropdown.Toggle
														id="assessment-export-button"
														className={`btn btn-icon btn-dropdown btn-move-record-white w-auto ${
															shouldHighlightAssessmentExportButton ? 'highlight-outline-secondary' : ''
														}`}
													>
														<SvgMask path="/svg/ellipsis-v.svg" width={24} height={24} />
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<Dropdown.Item as="button" onClick={confirmExport}>
															Export data
														</Dropdown.Item>

														<Dropdown.Item
															as="button"
															onClick={() => {
																setShowMoveRecordCheckboxes(true);
																setAssessmentTypeFilter('AssessmentsOverview');
															}}
														>
															Move records
														</Dropdown.Item>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										)}

										<div id="benchmark-filter" className="form-group assessment-filter-select-wrapper mb-4">
											<label htmlFor="benchmarkFilter" className="body-small mb-1">
												<strong>Benchmark Filter</strong>
											</label>

											<select
												value={benchmarkLevel ?? ''}
												onChange={(e) => setBenchmarkLevel(e.target.value as AssessmentBenchmarkLevel)}
												className={`form-select ${shouldHighlightBenchmarkFilter ? 'highlight-outline-secondary' : ''}`}
												name="className"
												id="benchmarkFilter"
												disabled={assessmentTypeFilter === 'Progress'}
											>
												<option value="">All benchmarks</option>

												{benchmarks.map((benchmark, index) => {
													const { key, label } = benchmark;
													return (
														<option key={index} value={key}>
															{label}
														</option>
													);
												})}
											</select>
										</div>
									</div>
								</div>

								{/* TABLE */}

								{assessmentTypeFilter === 'AssessmentsOverview' ? (
									<AssessmentOverviewTable
										shouldHighlightInFTUX={shouldHighlightPreselectStudentButton}
										assessmentSummaryParams={assessmentSummaryParams}
										setAssessmentSummaryParams={setAssessmentSummaryParams}
										onStudentClick={handleStudentClick}
										showMoveRecordCheckboxes={showMoveRecordCheckboxes}
										selectedMovingStudents={selectedMovingStudents}
										setSelectedMovingStudents={setSelectedMovingStudents}
									/>
								) : assessmentTypeFilter === 'GraphemePhonemeKnowledge' ? (
									<GraphemePhonemeTable
										assessmentGraphemePhonemeParams={assessmentGraphemePhonemeParams}
										setAssessmentGraphemePhonemeParams={setAssessmentGraphemePhonemeParams}
										onStudentClick={handleStudentClick}
									/>
								) : assessmentTypeFilter === 'Reading' ? (
									<ReadingTable
										assessmentReadingTableParams={assessmentReadingTableParams}
										setAssessmentReadingTableParams={setAssessmentReadingTableParams}
										onStudentClick={handleStudentClick}
									/>
								) : assessmentTypeFilter === 'Spelling' ? (
									<SpellingTable
										assessmentSpellingTableParams={assessmentSpellingTableParams}
										setAssessmentSpellingTableParams={setAssessmentSpellingTableParams}
										onStudentClick={handleStudentClick}
									/>
								) : assessmentTypeFilter === 'StageOverview' ? (
									<StageOverviewTable
										assessmentStageOverviewParams={assessmentStageOverviewParams}
										setAssessmentStageOverviewParams={setAssessmentStageOverviewParams}
										onStudentClick={handleStudentClick}
									/>
								) : assessmentTypeFilter === 'Progress' ? (
									<StudentProgressTable
										assessmentStudentProgressParams={assessmentStudentProgressParams}
										setAssessmentStudentProgressParams={setAssessmentStudentProgressParams}
										onStudentClick={handleStudentClick}
									/>
								) : null}
							</>
						) : null}
					</>
				)}
			</div>

			{/* RESOURCE MODAL */}
			<Modal show={showResourceModal} onHide={() => setShowResourceModal(false)} centered>
				<Modal.Body>
					<h2 className="mb-4 text-shades-800">
						<strong>Choose resource to download</strong>
					</h2>

					{resourceDocuments.categories?.map((category, categoryIndex) => {
						return (
							<div key={categoryIndex} className="mb-3">
								<p className="h4 mb-2 fw-bold text-shades-800">{category?.name}</p>

								{category.categoryItems?.map((item, itemIndex) => {
									return (
										<a key={itemIndex} download href={item.url} className="btn btn-primary w-100 mb-2">
											<SvgMask path={'/svg/download.svg'} width={16} height={16} />

											<span>{item.title}</span>
										</a>
									);
								})}
							</div>
						);
					})}

					<div className="d-flex button-group mt-0">
						<button onClick={() => setShowResourceModal(false)} className="btn btn-white btn-lg w-100">
							Cancel
						</button>
					</div>
				</Modal.Body>
			</Modal>

			{/* ASSESSMENT RECORDS */}
			<Offcanvas
				bsPrefix="view-teacher offcanvas"
				show={showEditAssessmentOffCanvas}
				onHide={() => setShowEditAssessmentOffCanvas(false)}
				onExited={() => setAssessmentRecordOptions(defaultAssessmentRecordOptions)}
				placement="end"
			>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title className="d-flex flex-column justify-content-center align-items-center w-100 gap-1">
						<div className="d-flex align-items-center gap-2">
							<h2 className="text-shades-800 mb-0">
								<strong>Assessment records</strong>
							</h2>
						</div>

						<p className="text-dark-text mb-0">Add, edit or view an assessment record for a student.</p>
					</Offcanvas.Title>
				</Offcanvas.Header>

				<Offcanvas.Body className="d-flex flex-column">
					{isLoading ? (
						<div className="d-flex align-items-center justify-content-center w-100">
							<DelayedFadeIn>
								<Spinner animation="border" size="sm">
									<span className="visually-hidden">Loading...</span>
								</Spinner>
							</DelayedFadeIn>
						</div>
					) : (
						<>
							<div className="d-flex justify-content-between gap-3 mb-4">
								<div className="form-group w-100">
									<label htmlFor="className" className="body-small mb-1">
										<strong>Class</strong>
									</label>

									<select
										value={assessmentRecordOptions.class}
										onChange={(e) => {
											const currentClass = assessmentRecordsDropdownLists.classList.find(
												(classDetail) => classDetail.id?.toLowerCase() === e.target.value.toLowerCase()
											);
											if (currentClass) {
												setAssessmentRecordOptions({
													class: currentClass?.id?.toLowerCase() || '',
													student: defaultAssessmentRecordOptions.student,
													assessmentType: defaultAssessmentRecordOptions.assessmentType,
													assessmentPeriod: defaultAssessmentRecordOptions.assessmentPeriod
												});
											}

											// Reset Assessment List
											setAssessmentRecordsDropdownLists({
												...assessmentRecordsDropdownLists,
												studentList: [],
												assessmentPeriodList: [],
												assessmentPeriodTypeList: [],
												assessmentList: []
											});
										}}
										className="form-select"
										name="classDetail"
										id="classDetail"
										disabled={isAssessmentLoading}
									>
										<option value="" disabled>
											Please select...
										</option>

										{assessmentRecordsDropdownLists.classList?.length &&
											assessmentRecordsDropdownLists.classList.map((classDetail, classIndex) => {
												return (
													<option key={classDetail.id || classIndex} value={classDetail.id?.toLowerCase()}>
														{classDetail.name}
													</option>
												);
											})}
									</select>
								</div>

								<div className={`form-group w-100 ${!assessmentRecordOptions.class ? 'opacity-0' : ''}`}>
									<label htmlFor="studentDetail" className="body-small mb-1">
										<strong>Student</strong>
									</label>

									<select
										value={assessmentRecordOptions.student}
										onChange={(e) => {
											const currentStudent = assessmentRecordsDropdownLists.studentList.find(
												(student) => student.id === e.target.value
											);

											if (currentStudent) {
												setAssessmentRecordOptions({
													...assessmentRecordOptions,
													student: currentStudent.id || '',
													assessmentType: defaultAssessmentRecordOptions.assessmentType,
													assessmentPeriod: defaultAssessmentRecordOptions.assessmentPeriod
												});
											}

											// Reset Assessment List
											setAssessmentRecordsDropdownLists({
												...assessmentRecordsDropdownLists,
												assessmentList: []
											});
										}}
										className="form-select"
										name="studentDetail"
										id="studentDetail"
										disabled={isAssessmentLoading || !assessmentRecordOptions.class}
									>
										<option value="" disabled>
											Please select...
										</option>

										{assessmentRecordsDropdownLists.studentList?.length &&
											assessmentRecordsDropdownLists.studentList.map((student, studentIndex) => {
												return (
													<option key={student.id || studentIndex} value={student.id}>
														{student.firstName} {student.lastName}
													</option>
												);
											})}
									</select>
								</div>
							</div>

							{assessmentRecordOptions.class && assessmentRecordOptions.student && (
								<>
									<div className="d-flex justify-content-between gap-3 mb-4">
										<div className={`form-group w-100 ${!assessmentRecordOptions.student ? 'opacity-0' : ''}`}>
											<label htmlFor="assessmentType" className="body-small mb-1">
												<strong>Assessment type</strong>
											</label>

											<select
												value={assessmentRecordOptions.assessmentType}
												onChange={(e) => {
													const currentAssessmentType = assessmentRecordsDropdownLists.assessmentPeriodTypeList.find(
														(assessmentType) => assessmentType.key === e.target.value
													);
													if (currentAssessmentType) {
														setAssessmentRecordOptions({
															...assessmentRecordOptions,
															assessmentType: currentAssessmentType.key || '',
															assessmentPeriod: defaultAssessmentRecordOptions.assessmentPeriod
														});
													}

													// Reset Assessment List
													setAssessmentRecordsDropdownLists({
														...assessmentRecordsDropdownLists,
														assessmentList: []
													});
												}}
												className="form-select"
												name="assessmentType"
												id="assessmentType"
												disabled={isAssessmentLoading || !assessmentRecordOptions.student}
											>
												<option value="" disabled>
													Please select...
												</option>

												{assessmentRecordsDropdownLists.assessmentPeriodTypeList?.length &&
													assessmentRecordsDropdownLists.assessmentPeriodTypeList.map(
														(assessmentType, assessmentTypeIndex) => {
															return (
																<option key={assessmentType.key || assessmentTypeIndex} value={assessmentType.key}>
																	{assessmentType.label}
																</option>
															);
														}
													)}
											</select>
										</div>

										<div className={`form-group w-100 ${!assessmentRecordOptions.assessmentType ? 'opacity-0' : ''}`}>
											<label htmlFor="assessmentPeriod" className="body-small mb-1">
												<strong>Assessment period</strong>
											</label>

											<select
												value={assessmentRecordOptions.assessmentPeriod}
												onChange={(e) => {
													const currentAssessmentPeriod = assessmentRecordsDropdownLists.assessmentPeriodList.find(
														(assessmentPeriod) => assessmentPeriod.key === e.target.value
													);

													if (currentAssessmentPeriod) {
														setAssessmentRecordOptions({
															...assessmentRecordOptions,
															assessmentPeriod: currentAssessmentPeriod.key || ''
														});
													}
												}}
												className="form-select"
												name="assessmentPeriod"
												id="assessmentPeriod"
												disabled={isAssessmentLoading || !assessmentRecordOptions.assessmentType}
											>
												<option value="" disabled>
													Please select...
												</option>

												{assessmentRecordsDropdownLists.assessmentPeriodList?.length &&
													assessmentRecordsDropdownLists.assessmentPeriodList.map(
														(assessmentPeriod, assessmentPeriodIndex) => {
															return (
																<option
																	key={assessmentPeriod.key || assessmentPeriodIndex}
																	value={assessmentPeriod.key}
																>
																	{assessmentPeriod.label}
																</option>
															);
														}
													)}
											</select>
										</div>
									</div>

									{assessmentRecordOptions.assessmentPeriod && (
										<div className="form-selection-wrapper mb-0">
											<div className="form-selection-wrapper-inner">
												{assessmentRecordsDropdownLists.assessmentList?.length > 0 &&
													assessmentRecordsDropdownLists.assessmentList.map((record, recordIndex) => {
														return (
															<button
																className="btn btn-row-label"
																key={record.id || recordIndex}
																onClick={() => navigateToRecord(record)}
															>
																<div className="d-flex gap-2">
																	<SvgMask path="/svg/assessment.svg" width={16} height={16} />

																	<strong>{record.name}</strong>
																</div>

																<div className="d-flex align-items-center gap-2">
																	{record?.previouslyMarked ? (
																		<span className="body-small">
																			<strong>Previously marked</strong>
																		</span>
																	) : (
																		<span className="body-small status-text">
																			<strong>Not assessed</strong>
																		</span>
																	)}

																	<div className="selected-icon-wrapper">
																		<SvgMask path="/svg/chevron-right.svg" width={16} height={16} />
																	</div>
																</div>
															</button>
														);
													})}
											</div>
										</div>
									)}
								</>
							)}

							<Divider />

							<div className="d-flex align-items-center justify-content-between gap-3">
								<p className="mb-0 body-small fw-semibold">
									If you have previously conducted assessments and would like to enter historic data in the platform,
									you can use the "Quick record" functionality to enter the historic stage of mastery.
								</p>

								<button
									className="btn text-nowrap"
									onClick={() => navigate('/assessment-management/assessment-record-multiple')}
								>
									<SvgMask path={'/svg/quick-record-add.svg'} width={16} height={16} />
									Quick record
								</button>
							</div>
						</>
					)}
				</Offcanvas.Body>
			</Offcanvas>

			{/* MOVE ASSESSMENTS MODAL */}
			<MoveAssessmentMultiple
				show={showMoveAssessmentsModal}
				onHide={() => setShowMoveAssessmentsModal(false)}
				selectedMovingStudents={selectedMovingStudents}
				onMoveSuccess={handleMoveSuccess}
			/>
			<DynamicFtux config={assessmentsConfig} tutorialType="Assessments" manualRestartTrigger={manualRestartTrigger} />
		</div>
	);
};

export default Assessments;
